import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { 
  Package, 
  CreditCard, 
  Building2, 
  Upload, 
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  Tag,
  X,
  Sparkles,
  Crown,
  Globe,
  Link2
} from 'lucide-react';
import { collection, addDoc, serverTimestamp, getDocs, doc, getDoc, query, where, updateDoc, increment } from 'firebase/firestore';
import { db } from '../../lib/firebase';

interface CheckoutData {
  plan: 'pro' | 'pro_plus';
  planName: string;
  price: number;
  billingCycle: 'monthly' | 'semi_annual' | 'yearly';
  hasPathUrl: boolean;
  hasSubdomain: boolean;
  username: string;
  urlAddonPrice: number;
  isProPlusYearly: boolean;
}

export default function SubscriptionCheckoutSummary() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [checkoutData, setCheckoutData] = useState<CheckoutData | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'promptpay' | 'bank'>('promptpay');
  const [paymentProof, setPaymentProof] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState<{id: string, code: string, percentage: number, amount: number} | null>(null);
  const [discountError, setDiscountError] = useState('');
  const [checkingDiscount, setCheckingDiscount] = useState(false);
  const [paymentSettings, setPaymentSettings] = useState<any>(null);

  useEffect(() => {
    if (!user) {
      navigate('/login', { state: { from: window.location.pathname + window.location.search } });
      return;
    }

    const plan = searchParams.get('plan') as 'pro' | 'pro_plus';
    const billing = searchParams.get('billing') as 'monthly' | 'semi_annual' | 'yearly';
    const hasPathUrl = searchParams.get('hasPathUrl') === 'true';
    const hasSubdomain = searchParams.get('hasSubdomain') === 'true';
    const username = searchParams.get('username') || '';

    if (!plan || !billing) {
      navigate('/subscription/select');
      return;
    }

    const basePrice = getBasePrice(plan, billing);
    const urlAddonPrice = calculateUrlAddonPrice(plan, billing, hasPathUrl, hasSubdomain);
    const isProPlusYearly = plan === 'pro_plus' && billing === 'yearly';

    const newCheckoutData: CheckoutData = {
      plan,
      planName: plan === 'pro' ? 'Pro' : 'Pro+',
      price: basePrice,
      billingCycle: billing,
      hasPathUrl,
      hasSubdomain,
      username,
      urlAddonPrice,
      isProPlusYearly
    };

    setCheckoutData(newCheckoutData);
    loadPaymentSettings();
  }, [user, navigate, searchParams]);

  const getBasePrice = (plan: 'pro' | 'pro_plus', billing: 'monthly' | 'semi_annual' | 'yearly') => {
    if (plan === 'pro') {
      if (billing === 'monthly') return 690;
      if (billing === 'semi_annual') return 3795;
      return 6900;
    } else {
      if (billing === 'monthly') return 1590;
      if (billing === 'semi_annual') return 8745;
      return 15900;
    }
  };

  const calculateUrlAddonPrice = (plan: 'pro' | 'pro_plus', billing: 'monthly' | 'semi_annual' | 'yearly', hasPathUrl: boolean, hasSubdomain: boolean) => {
    let total = 0;
    const isProPlusYearly = plan === 'pro_plus' && billing === 'yearly';
    
    if (hasPathUrl) {
      if (billing === 'monthly') total += 49;
      else if (billing === 'semi_annual') total += 245;
      else total += 490;
    }
    
    if (hasSubdomain && !isProPlusYearly) {
      if (billing === 'monthly') total += 99;
      else if (billing === 'semi_annual') total += 495;
      else total += 990;
    }
    
    return total;
  };

  const loadPaymentSettings = async () => {
    try {
      const settingsDoc = await getDoc(doc(db, 'paymentSettings', 'default'));
      if (settingsDoc.exists()) {
        setPaymentSettings(settingsDoc.data());
      }
    } catch (error) {
      console.error('Error loading payment settings:', error);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setPaymentProof(e.target.files[0]);
    }
  };

  const handleApplyDiscount = async () => {
    setDiscountError('');
    setCheckingDiscount(true);

    try {
      const code = discountCode.trim().toUpperCase();
      
      const discountsQuery = query(
        collection(db, 'discounts'),
        where('code', '==', code),
        where('isActive', '==', true)
      );
      
      const discountsSnap = await getDocs(discountsQuery);
      
      if (!discountsSnap.empty) {
        const discountDoc = discountsSnap.docs[0];
        const discount = discountDoc.data();
        
        if (discount.expirationDate) {
          const expirationDate = discount.expirationDate.toDate();
          const now = new Date();
          
          if (expirationDate < now) {
            setDiscountError('รหัสส่วนลดนี้หมดอายุแล้ว');
            setAppliedDiscount(null);
            setCheckingDiscount(false);
            return;
          }
        }
        
        const totalPrice = (checkoutData?.price || 0) + (checkoutData?.urlAddonPrice || 0);
        const discountAmount = Math.round(totalPrice * (discount.percentage / 100));
        
        setAppliedDiscount({
          id: discountDoc.id,
          code: discount.code,
          percentage: discount.percentage,
          amount: discountAmount
        });
        setDiscountError('');
      } else {
        setDiscountError('รหัสส่วนลดไม่ถูกต้องหรือหมดอายุแล้ว');
        setAppliedDiscount(null);
      }
    } catch (error) {
      console.error('Error applying discount:', error);
      setDiscountError('เกิดข้อผิดพลาดในการตรวจสอบรหัสส่วนลด');
      setAppliedDiscount(null);
    } finally {
      setCheckingDiscount(false);
    }
  };

  const handleRemoveDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode('');
    setDiscountError('');
  };

  const calculateFinalPrice = () => {
    if (!checkoutData) return 0;
    const baseTotal = checkoutData.price + checkoutData.urlAddonPrice;
    if (appliedDiscount) {
      return baseTotal - appliedDiscount.amount;
    }
    return baseTotal;
  };

  const handleSubmitPayment = async () => {
    if (!paymentProof) {
      alert('กรุณาอัปโหลดหลักฐานการชำระเงิน');
      return;
    }

    if (!user || !checkoutData) {
      alert('กรุณาเข้าสู่ระบบก่อน');
      return;
    }

    setUploading(true);

    try {
      await addDoc(collection(db, 'subscriptionPaymentNotifications'), {
        userId: user.uid,
        userEmail: user.email,
        displayName: user.displayName || user.email,
        plan: checkoutData.plan,
        planName: checkoutData.planName,
        baseAmount: checkoutData.price,
        urlAddonAmount: checkoutData.urlAddonPrice,
        totalAmount: checkoutData.price + checkoutData.urlAddonPrice,
        finalAmount: calculateFinalPrice(),
        billingCycle: checkoutData.billingCycle,
        hasPathUrl: checkoutData.hasPathUrl,
        hasSubdomain: checkoutData.hasSubdomain,
        username: checkoutData.username,
        discountCode: appliedDiscount?.code || null,
        discountPercentage: appliedDiscount?.percentage || null,
        discountAmount: appliedDiscount?.amount || null,
        paymentMethod,
        isRenewal: false,
        userType: 'new_subscription',
        status: 'pending',
        createdAt: serverTimestamp(),
        paymentProofName: paymentProof.name,
      });

      if (appliedDiscount?.id) {
        await updateDoc(doc(db, 'discounts', appliedDiscount.id), {
          usageCount: increment(1)
        });
      }

      setShowSuccess(true);

      setTimeout(() => {
        window.location.href = '/subscription/manage';
      }, 3000);
    } catch (error) {
      console.error('Error submitting payment:', error);
      alert('เกิดข้อผิดพลาดในการส่งข้อมูล กรุณาลองใหม่อีกครั้ง');
    } finally {
      setUploading(false);
    }
  };

  const getBillingLabel = () => {
    if (!checkoutData) return '';
    if (checkoutData.billingCycle === 'monthly') return 'รายเดือน';
    if (checkoutData.billingCycle === 'semi_annual') return 'ราย 6 เดือน';
    return 'รายปี';
  };

  const getSavingsAmount = () => {
    if (!checkoutData) return 0;
    const baseMonthly = checkoutData.plan === 'pro' ? 690 : 1590;
    if (checkoutData.billingCycle === 'semi_annual') return baseMonthly;
    if (checkoutData.billingCycle === 'yearly') return baseMonthly * 2;
    return 0;
  };

  if (!checkoutData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/20 to-purple-50/20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  if (showSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/20 to-purple-50/20 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">แจ้งชำระเงินสำเร็จ!</h2>
          <p className="text-gray-600 mb-6">
            เราได้รับการแจ้งชำระเงินของคุณแล้ว<br />
            ทีมงานจะตรวจสอบภายใน 24 ชั่วโมง
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-blue-800 font-semibold mb-2">
              ⚡ ต้องการความรวดเร็ว?
            </p>
            <p className="text-sm text-blue-700">
              ติดต่อเราได้ที่ LINE: <a href="https://line.me/ti/p/~@edumi" target="_blank" rel="noopener noreferrer" className="font-bold underline hover:text-blue-900">@edumi</a>
            </p>
          </div>
          <p className="text-sm text-gray-500">
            คุณจะได้รับอีเมลยืนยันเมื่อการชำระเงินได้รับการอนุมัติ
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/20 to-purple-50/20 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <button
          onClick={() => navigate('/subscription/select')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          กลับ
        </button>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <CreditCard className="w-8 h-8 text-blue-600" />
            สรุปการชำระเงิน
          </h1>
          <p className="text-gray-600 mt-2">ตรวจสอบรายการและชำระเงิน</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - Order Summary */}
          <div className="lg:col-span-2 space-y-6">
            {/* Order Details */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Package className="w-5 h-5 text-blue-600" />
                รายการที่เลือก
              </h2>

              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-4 border-2 border-blue-200">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-gray-600">แพ็คเก็จคอร์สเรียน</p>
                    <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                      {checkoutData.plan === 'pro' ? (
                        <Crown className="w-5 h-5 text-blue-600" />
                      ) : (
                        <Sparkles className="w-5 h-5 text-blue-600" />
                      )}
                      {checkoutData.planName}
                    </h3>
                    <p className="text-sm text-blue-700 font-semibold mt-1 flex items-center gap-1">
                      {checkoutData.billingCycle === 'yearly' ? (
                        <>
                          <Sparkles className="w-4 h-4" />
                          รายปี (ฟรี 2 เดือน)
                        </>
                      ) : checkoutData.billingCycle === 'semi_annual' ? (
                        <>
                          <Sparkles className="w-4 h-4" />
                          ราย 6 เดือน (ฟรี 1 เดือน)
                        </>
                      ) : (
                        'รายเดือน'
                      )}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-blue-600">
                      ฿{checkoutData.price.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500">
                      {checkoutData.billingCycle === 'yearly' ? '/ ปี' : checkoutData.billingCycle === 'semi_annual' ? '/ 6 เดือน' : '/ เดือน'}
                    </p>
                    {checkoutData.billingCycle !== 'monthly' && (
                      <p className="text-xs text-blue-600 font-semibold mt-1">
                        เฉลี่ย {Math.round(checkoutData.price / (checkoutData.billingCycle === 'yearly' ? 12 : 6))}฿/เดือน
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* URL Add-ons */}
              {(checkoutData.hasPathUrl || checkoutData.hasSubdomain) && (
                <div className="mt-4 space-y-3">
                  <h3 className="text-sm font-semibold text-gray-700">URL Add-ons:</h3>
                  
                  {checkoutData.hasPathUrl && (
                    <div className="flex items-center justify-between p-3 bg-orange-50 border border-orange-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Link2 className="w-4 h-4 text-orange-600" />
                        <div>
                          <p className="text-sm font-semibold text-gray-900">Path URL</p>
                          <p className="text-xs text-gray-600 font-mono">edumi.co/{checkoutData.username}</p>
                        </div>
                      </div>
                      <p className="text-sm font-bold text-orange-600">
                        ฿{checkoutData.billingCycle === 'monthly' ? '49' : checkoutData.billingCycle === 'semi_annual' ? '245' : '490'}
                      </p>
                    </div>
                  )}

                  {checkoutData.hasSubdomain && (
                    <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-blue-600" />
                        <div>
                          <p className="text-sm font-semibold text-gray-900">Subdomain</p>
                          <p className="text-xs text-gray-600 font-mono">{checkoutData.username}.edumi.co</p>
                        </div>
                      </div>
                      {checkoutData.isProPlusYearly ? (
                        <p className="text-sm font-bold text-green-600">ฟรี!</p>
                      ) : (
                        <p className="text-sm font-bold text-blue-600">
                          ฿{checkoutData.billingCycle === 'monthly' ? '99' : checkoutData.billingCycle === 'semi_annual' ? '495' : '990'}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Discount Code Section */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Tag className="w-5 h-5 text-blue-600" />
                รหัสส่วนลด
              </h2>

              {appliedDiscount ? (
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="font-bold text-gray-900">{appliedDiscount.code}</p>
                        <p className="text-sm text-green-700">ส่วนลด {appliedDiscount.percentage}% (฿{appliedDiscount.amount.toLocaleString()})</p>
                      </div>
                    </div>
                    <button
                      onClick={handleRemoveDiscount}
                      className="p-2 hover:bg-red-100 rounded-lg transition-colors"
                    >
                      <X className="w-5 h-5 text-red-600" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={discountCode}
                      onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                      onKeyPress={(e) => e.key === 'Enter' && handleApplyDiscount()}
                      placeholder="กรอกรหัสส่วนลด"
                      className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none font-semibold"
                    />
                    <button
                      onClick={handleApplyDiscount}
                      disabled={!discountCode.trim() || checkingDiscount}
                      className={`px-6 py-3 rounded-xl font-bold transition-all ${
                        !discountCode.trim() || checkingDiscount
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700'
                      }`}
                    >
                      {checkingDiscount ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      ) : (
                        'ใช้'
                      )}
                    </button>
                  </div>
                  {discountError && (
                    <div className="flex items-center gap-2 text-red-600 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      {discountError}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Payment Method */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-blue-600" />
                วิธีการชำระเงิน
              </h2>

              <div className="space-y-3 mb-6">
                <button
                  onClick={() => setPaymentMethod('promptpay')}
                  className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                    paymentMethod === 'promptpay'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      paymentMethod === 'promptpay' ? 'border-blue-500' : 'border-gray-300'
                    }`}>
                      {paymentMethod === 'promptpay' && (
                        <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      )}
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">PromptPay</p>
                      <p className="text-sm text-gray-600">โอนผ่าน QR Code</p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => setPaymentMethod('bank')}
                  className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                    paymentMethod === 'bank'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      paymentMethod === 'bank' ? 'border-blue-500' : 'border-gray-300'
                    }`}>
                      {paymentMethod === 'bank' && (
                        <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      )}
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">โอนเงินผ่านธนาคาร</p>
                      <p className="text-sm text-gray-600">โอนเข้าบัญชีธนาคาร</p>
                    </div>
                  </div>
                </button>
              </div>

              {/* Payment Details */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="w-5 h-5 text-gray-600" />
                  <h3 className="font-bold text-gray-900">ข้อมูลการโอนเงิน</h3>
                </div>
                {paymentMethod === 'promptpay' ? (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">PromptPay ID:</span>
                      <span className="font-semibold text-gray-900">{paymentSettings?.promptpay?.id || '0XX-XXX-XXXX'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">ชื่อบัญชี:</span>
                      <span className="font-semibold text-gray-900">{paymentSettings?.promptpay?.name || 'EduMi Platform'}</span>
                    </div>
                    {paymentSettings?.promptpay?.qrCodeUrl && (
                      <div className="mt-4 p-3 bg-white rounded-lg border border-gray-200">
                        <p className="text-xs text-gray-600 mb-2 text-center">สแกน QR Code เพื่อชำระเงิน</p>
                        <img 
                          src={paymentSettings.promptpay.qrCodeUrl} 
                          alt="PromptPay QR Code" 
                          className="w-48 h-48 object-contain mx-auto"
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">ธนาคาร:</span>
                      <span className="font-semibold text-gray-900">{paymentSettings?.bank?.bankName || 'ธนาคารกสิกรไทย'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">เลขที่บัญชี:</span>
                      <span className="font-semibold text-gray-900">{paymentSettings?.bank?.accountNumber || 'XXX-X-XXXXX-X'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">ชื่อบัญชี:</span>
                      <span className="font-semibold text-gray-900">{paymentSettings?.bank?.accountName || 'EduMi Platform'}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Upload Payment Proof */}
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center">
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-sm font-semibold text-gray-700 mb-2">
                  อัปโหลดหลักฐานการชำระเงิน
                </p>
                <p className="text-xs text-gray-500 mb-4">
                  รองรับไฟล์ JPG, PNG (ขนาดไม่เกิน 5MB)
                </p>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                  id="payment-proof"
                />
                <label
                  htmlFor="payment-proof"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 cursor-pointer transition-all"
                >
                  <Upload className="w-4 h-4" />
                  เลือกไฟล์
                </label>
                {paymentProof && (
                  <p className="text-sm text-green-600 mt-3 flex items-center justify-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    {paymentProof.name}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Summary & Submit */}
          <div className="lg:col-span-1">
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl shadow-lg p-6 border-2 border-blue-200 sticky top-4">
              <h2 className="text-xl font-bold text-gray-900 mb-4">สรุปยอดชำระ</h2>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">แพ็คเก็จ {checkoutData.planName}</span>
                  <span className="font-semibold text-gray-900">
                    ฿{checkoutData.price.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">รอบชำระเงิน</span>
                  <span className="font-semibold text-blue-700">
                    {getBillingLabel()}
                  </span>
                </div>
                {checkoutData.urlAddonPrice > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">URL Add-ons</span>
                    <span className="font-semibold text-gray-900">
                      ฿{checkoutData.urlAddonPrice.toLocaleString()}
                    </span>
                  </div>
                )}
                {checkoutData.billingCycle !== 'monthly' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-2 text-xs">
                    <p className="text-green-800 font-semibold flex items-center gap-1">
                      <Sparkles className="w-3 h-3" />
                      ฟรี {checkoutData.billingCycle === 'yearly' ? '2' : '1'} เดือน!
                    </p>
                    <p className="text-green-700">
                      เฉลี่ย {Math.round((checkoutData.price + checkoutData.urlAddonPrice) / (checkoutData.billingCycle === 'yearly' ? 12 : 6))}฿/เดือน
                    </p>
                    {checkoutData.isProPlusYearly && (
                      <p className="text-green-700 mt-1">+ Subdomain ฟรี (มูลค่า 990฿)</p>
                    )}
                  </div>
                )}
                {appliedDiscount && (
                  <div className="flex justify-between text-sm">
                    <span className="text-green-600 flex items-center gap-1">
                      <Tag className="w-4 h-4" />
                      ส่วนลด ({appliedDiscount.percentage}%)
                    </span>
                    <span className="font-semibold text-green-600">-฿{appliedDiscount.amount.toLocaleString()}</span>
                  </div>
                )}
                <div className="border-t border-blue-200 pt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-gray-900">ยอดรวมทั้งหมด</span>
                    <span className="text-3xl font-bold text-blue-600">
                      ฿{calculateFinalPrice().toLocaleString()}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 text-right mt-1">
                    {checkoutData.billingCycle === 'yearly' ? 'ต่อปี' : checkoutData.billingCycle === 'semi_annual' ? 'ต่อ 6 เดือน' : 'ต่อเดือน'}
                  </p>
                </div>
              </div>

              {/* Important Notes */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-amber-800">
                    <p className="font-semibold mb-1">ข้อควรทราบ:</p>
                    <ul className="list-disc list-inside space-y-1 text-xs">
                      <li>โอนเงินตามยอดที่ระบุ</li>
                      <li>อัปโหลดหลักฐานการโอนเงิน</li>
                      <li>รอการตรวจสอบภายใน 24 ชม.</li>
                      <li>จะได้รับอีเมลยืนยัน</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <button
                onClick={handleSubmitPayment}
                disabled={!paymentProof || uploading}
                className={`w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-bold text-lg shadow-lg transition-all ${
                  !paymentProof || uploading
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white hover:shadow-xl'
                }`}
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    กำลังส่งข้อมูล...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    เริ่มทดลองใช้ฟรี
                  </>
                )}
              </button>

              <p className="text-xs text-gray-500 text-center mt-4">
                กดปุ่มเพื่อแจ้งชำระเงินและรอการอนุมัติ
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
